const MOBILE_MQ = window.matchMedia("(max-width: 768px)");
const OPEN_CLASS = "is-open";
const ACTIVE_CLASS = "is-active";
const VISIBLE_CLASS = "is-visible";
const SINGLE_OPTION_CLASS = "is-single-option";
const BODY_LOCK_CLASS = "subscription-sheet-open";
const BODY_STICKY_CLASS = "has-sticky-cta";
const RECHARGE_BLOCK_SELECTOR = ".shopify-block.recharge-subscription-widget";
const RECHARGE_HOST_SELECTOR =
  "recharge-subscription-widget, rc-widget, [class*='rc-widget']";
const SYNC_DEBOUNCE_MS = 120;
const MOUNT_RETRY_MS = 200;
const MAX_MOUNT_RETRIES = 5;
const CONFIG_OBSERVER_DEBOUNCE_MS = 80;

function getRechargeWidgetConfig() {
  return window.SubscriptionWidgetConfig || null;
}

class SubscriptionPdpPanel extends HTMLElement {
  constructor() {
    super();
    this._initialized = false;
    this._sheetOpen = false;
    this._sectionId = "";
    this._trigger = null;
    this._backdrop = null;
    this._stickyCta = null;
    this._boundHandlers = {};
  }

  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    this._sectionId = this.dataset.sectionId || this.id.replace("subscription-wrapper-", "");
    this._trigger = this.querySelector(".js-subscription-accordion-trigger");
    this._backdrop = this.querySelector(".js-subscription-backdrop");
    this._stickyCta = document.querySelector(
      `sticky-subscription-cta[data-section-id="${this._sectionId}"]`,
    );

    this._bindHandlers();
    this._bindEvents();
    this._initState();

    document.addEventListener("shopify:section:unload", this._boundHandlers.sectionUnload);
  }

  disconnectedCallback() {
    this._initialized = false;
    this._unbindEvents();
    document.body.classList.remove(BODY_LOCK_CLASS);
    document.removeEventListener("shopify:section:unload", this._boundHandlers.sectionUnload);
  }

  _bindHandlers() {
    this._boundHandlers = {
      triggerClick: (event) => this._handleTriggerClick(event),
      backdropClick: () => this.close(true),
      keydown: (event) => this._handleKeydown(event),
      mqChange: () => this._handleMqChange(),
      sectionUnload: (event) => this._handleSectionUnload(event),
    };
  }

  _bindEvents() {
    this._trigger?.addEventListener("click", this._boundHandlers.triggerClick);
    this._backdrop?.addEventListener("click", this._boundHandlers.backdropClick);
    document.addEventListener("keydown", this._boundHandlers.keydown);
    MOBILE_MQ.addEventListener("change", this._boundHandlers.mqChange);
  }

  _unbindEvents() {
    this._trigger?.removeEventListener("click", this._boundHandlers.triggerClick);
    this._backdrop?.removeEventListener("click", this._boundHandlers.backdropClick);
    document.removeEventListener("keydown", this._boundHandlers.keydown);
    MOBILE_MQ.removeEventListener("change", this._boundHandlers.mqChange);
  }

  _handleSectionUnload(event) {
    const section = this.closest(".shopify-section");
    if (!section || !section.contains(event.target)) return;
    this.close(false);
  }

  _initState() {
    if (this._isSheetMode()) {
      this._sheetOpen = false;
      this.classList.remove(OPEN_CLASS);
      this._syncBackdrop(false);
      this._setExpanded(false);
      this._stickyCta?.classList.remove(ACTIVE_CLASS);
      document.body.classList.remove(BODY_LOCK_CLASS);
      return;
    }

    this.classList.add(OPEN_CLASS);
    this._setExpanded(true);
    this._syncBackdrop(false);
  }

  _handleMqChange() {
    if (this._isSheetMode()) {
      this._sheetOpen = false;
      this.classList.remove(OPEN_CLASS);
      this._syncBackdrop(false);
      this._setExpanded(false);
      this._stickyCta?.classList.remove(ACTIVE_CLASS);
      document.body.classList.remove(BODY_LOCK_CLASS);
      return;
    }

    this._sheetOpen = false;
    this._stickyCta?.classList.remove(ACTIVE_CLASS);
    document.body.classList.remove(BODY_LOCK_CLASS);
    this._syncBackdrop(false);
    this.classList.add(OPEN_CLASS);
    this._setExpanded(true);
  }

  _isMobile() {
    return MOBILE_MQ.matches;
  }

  _isSheetMode() {
    return (
      this._isMobile() &&
      this.dataset.sheetMode === "true" &&
      !this.classList.contains(SINGLE_OPTION_CLASS)
    );
  }

  isSheetOpen() {
    return this._sheetOpen;
  }

  open() {
    if (!this._isSheetMode()) return;

    this._sheetOpen = true;
    this.classList.add(OPEN_CLASS);
    document.body.classList.add(BODY_LOCK_CLASS);
    this._stickyCta?.classList.add(ACTIVE_CLASS);
    this._syncBackdrop(true);
    this._setExpanded(true);

    requestAnimationFrame(() => {
      const rechargeBlock = this.querySelector(RECHARGE_BLOCK_SELECTOR);
      const root = rechargeBlock ? this._getRechargeQueryRoot(rechargeBlock) : null;
      const focusTarget =
        root?.querySelector('input[type="radio"]:checked') ||
        this._trigger;
      focusTarget?.focus?.({ preventScroll: true });
    });
  }

  close(returnFocus) {
    if (!this._isSheetMode()) return;

    this._sheetOpen = false;
    this.classList.remove(OPEN_CLASS);
    document.body.classList.remove(BODY_LOCK_CLASS);
    this._stickyCta?.classList.remove(ACTIVE_CLASS);
    this._syncBackdrop(false);
    this._setExpanded(false);

    if (returnFocus) {
      this._stickyCta
        ?.querySelector("[data-sticky-cta-toggle]")
        ?.focus?.({ preventScroll: true });
    }
  }

  toggleSheet() {
    if (this._sheetOpen) this.close(true);
    else this.open();
  }

  _handleTriggerClick(event) {
    if (this._isSheetMode()) {
      event.preventDefault();
      this.close(true);
      return;
    }

    const expanded = !this.classList.contains(OPEN_CLASS);
    this.classList.toggle(OPEN_CLASS, expanded);
    this._setExpanded(expanded);
  }

  _handleKeydown(event) {
    if (event.key !== "Escape" || !this._sheetOpen) return;
    this.close(true);
  }

  _syncBackdrop(visible) {
    if (!this._backdrop) return;

    this._backdrop.setAttribute("aria-hidden", visible ? "false" : "true");
    this._backdrop.toggleAttribute("hidden", !visible);
  }

  _setExpanded(expanded) {
    this._trigger?.setAttribute("aria-expanded", expanded ? "true" : "false");
    this._stickyCta
      ?.querySelector("[data-sticky-cta-toggle]")
      ?.setAttribute("aria-expanded", expanded ? "true" : "false");
  }

  _getRechargeShadowHost(block) {
    if (!block) return null;
    return (
      block.querySelector(RECHARGE_HOST_SELECTOR) ||
      block.firstElementChild ||
      block
    );
  }

  _getRechargeQueryRoot(block) {
    if (!block) return null;
    const host = this._getRechargeShadowHost(block);
    if (host?.shadowRoot) return host.shadowRoot;
    if (block.shadowRoot) return block.shadowRoot;
    return host || block;
  }
}

class StickySubscriptionCta extends HTMLElement {
  constructor() {
    super();
    this._open = false;
    this._initialized = false;
    this._watchBound = false;
    this._mountObserver = null;
    this._configObserver = null;
    this._configObserverTimer = null;
    this._mountRetryTimer = null;
    this._mountRetryCount = 0;
    this._syncTimer = null;
    this._lastSummaryKey = "";
    this._sectionId = "";
    this._panel = null;
    this._toggleBtn = null;
    this._planEl = null;
    this._priceEl = null;
    this._badgeEl = null;
    this._liveEl = null;
    this._variantUnsub = null;
    this._boundHandlers = {};
    this._rechargeListeners = [];
    this._resizeObserver = null;
  }

  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    this._sectionId = this.dataset.sectionId || "";
    this._panel = document.getElementById(`subscription-wrapper-${this._sectionId}`);
    if (!this._panel) return;

    this._toggleBtn = this.querySelector("[data-sticky-cta-toggle]");
    this._planEl = this.querySelector("[data-sticky-plan]");
    this._priceEl = this.querySelector("[data-sticky-price]");
    this._badgeEl = this.querySelector("[data-sticky-badge]");
    this._liveEl = this.querySelector("[data-sticky-live]");

    this._bindHandlers();
    this._bindEvents();
    this._observeStickyHeight();

    if (this._isMobile()) {
      this._bootstrap();

      if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
        this._variantUnsub = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event?.data?.sectionId !== this._sectionId) return;
          this._closeSheet(false);
          this._lastSummaryKey = "";
          this._scheduleUpdate();
        });
      }
    }

    document.addEventListener("shopify:section:load", this._boundHandlers.sectionLoad);
    document.addEventListener("shopify:section:unload", this._boundHandlers.sectionUnload);
  }

  disconnectedCallback() {
    this._initialized = false;
    this._watchBound = false;
    this._teardown();
    document.removeEventListener("shopify:section:load", this._boundHandlers.sectionLoad);
    document.removeEventListener("shopify:section:unload", this._boundHandlers.sectionUnload);
  }

  _bindHandlers() {
    this._boundHandlers = {
      toggleClick: (event) => this._handleToggleClick(event),
      sectionLoad: (event) => this._handleSectionLoad(event),
      sectionUnload: (event) => this._handleSectionUnload(event),
      hostInteraction: () => this._scheduleUpdate(),
      mqChange: () => {
        if (!this._isMobile()) {
          this._open = false;
          this.classList.remove(ACTIVE_CLASS);
          this._syncBodyStickyState(true);
        } else if (!this._watchBound) {
          this._bootstrap();
        }
        this._scheduleUpdate();
      },
    };
  }

  _bindEvents() {
    this._toggleBtn?.addEventListener("click", this._boundHandlers.toggleClick);
    MOBILE_MQ.addEventListener("change", this._boundHandlers.mqChange);
  }

  _unbindEvents() {
    this._toggleBtn?.removeEventListener("click", this._boundHandlers.toggleClick);
    MOBILE_MQ.removeEventListener("change", this._boundHandlers.mqChange);
    this._detachRechargeListeners();
  }

  _teardown() {
    this._clearMountRetries();
    this._clearSyncTimer();
    this._clearConfigObserverTimer();
    this._mountObserver?.disconnect();
    this._mountObserver = null;
    this._configObserver?.disconnect();
    this._configObserver = null;
    this._resizeObserver?.disconnect();
    this._resizeObserver = null;
    this._syncBodyStickyState(false);
    this._unbindEvents();
    if (typeof this._variantUnsub === "function") this._variantUnsub();
    this._variantUnsub = null;
  }

  _handleSectionLoad(event) {
    const section = this.closest(".shopify-section");
    if (!section || !section.contains(event.target)) return;
    if (!this._isMobile()) return;

    this._panel = document.getElementById(`subscription-wrapper-${this._sectionId}`);
    this._watchBound = false;
    this._configObserver?.disconnect();
    this._configObserver = null;
    this._mountObserver?.disconnect();
    this._mountObserver = null;
    this._lastSummaryKey = "";
    this._bootstrap();
  }

  _handleSectionUnload(event) {
    const section = this.closest(".shopify-section");
    if (!section || !section.contains(event.target)) return;
    this._teardown();
  }

  _bootstrap() {
    this._evaluateOptionCount();
    this._scheduleUpdate();
    this._bindFormInputListeners();
    this._watchForRechargeConfigOnce();
    this._scheduleMountRetries();
  }

  _getSectionScope() {
    return this._panel?.closest(".shopify-section") || this._panel || this;
  }

  _getVariantId() {
    const scope = this._getSectionScope();
    if (!scope) return "";

    const idInput =
      scope.querySelector('form[action*="/cart/add"] input[name="id"]') ||
      scope.querySelector('input[name="id"]');
    return idInput?.value || "";
  }

  _getSellingPlanId() {
    const scope = this._getSectionScope();
    if (!scope) return "";

    const planInput =
      scope.querySelector('form[action*="/cart/add"] input[name="selling_plan"]') ||
      scope.querySelector('input[name="selling_plan"]');
    return planInput?.value || "";
  }

  _clearConfigObserverTimer() {
    if (this._configObserverTimer) {
      window.clearTimeout(this._configObserverTimer);
      this._configObserverTimer = null;
    }
  }

  _scheduleConfigObserverUpdate() {
    this._clearConfigObserverTimer();
    this._configObserverTimer = window.setTimeout(() => {
      this._configObserverTimer = null;
      this._scheduleUpdate();
    }, CONFIG_OBSERVER_DEBOUNCE_MS);
  }

  _bindFormInputListeners() {
    if (this._watchBound) return;

    const scope = this._getSectionScope();
    if (!scope) return;

    this._watchBound = true;
    this._detachRechargeListeners();

    const attach = (target) => {
      if (!target) return;
      target.addEventListener("change", this._boundHandlers.hostInteraction, true);
      target.addEventListener("input", this._boundHandlers.hostInteraction, true);
      this._rechargeListeners.push(
        { target, type: "change", handler: this._boundHandlers.hostInteraction, options: true },
        { target, type: "input", handler: this._boundHandlers.hostInteraction, options: true },
      );
    };

    attach(scope);
  }

  _watchForRechargeConfigOnce() {
    if (this._configObserver) return;

    const scope = this._getSectionScope();
    if (!scope) return;

    this._configObserver = new MutationObserver(() => {
      this._scheduleConfigObserverUpdate();
    });

    this._configObserver.observe(scope, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ["value"],
    });

    if (this._mountObserver) return;

    this._mountObserver = new MutationObserver(() => {
      if (getRechargeWidgetConfig()?.formattedPrices) {
        this._scheduleConfigObserverUpdate();
      }
    });

    this._mountObserver.observe(scope, {
      childList: true,
      subtree: true,
    });
  }

  _observeStickyHeight() {
    if (this._resizeObserver || typeof ResizeObserver === "undefined") return;

    this._resizeObserver = new ResizeObserver(() => {
      this._syncBodyStickyState();
    });
    this._resizeObserver.observe(this);
  }

  _syncBodyStickyState(forceRemove) {
    if (!this._isMobile() || forceRemove) {
      document.body.classList.remove(BODY_STICKY_CLASS);
      document.body.style.removeProperty("--sticky-cta-height");
      return;
    }

    const isVisible =
      !this.hidden &&
      this.classList.contains(VISIBLE_CLASS) &&
      !this.classList.contains(ACTIVE_CLASS) &&
      !this._open &&
      !document.body.classList.contains(BODY_LOCK_CLASS);

    if (!isVisible) {
      document.body.classList.remove(BODY_STICKY_CLASS);
      return;
    }

    document.body.classList.add(BODY_STICKY_CLASS);

    const height = this.offsetHeight;
    if (height > 0) {
      document.body.style.setProperty("--sticky-cta-height", `${height}px`);
    }
  }

  _isMobile() {
    return MOBILE_MQ.matches;
  }

  _scheduleUpdate() {
    this._clearSyncTimer();
    this._syncTimer = window.setTimeout(() => {
      this._syncTimer = null;
      this._evaluateOptionCount();
      this._syncSummary();
    }, SYNC_DEBOUNCE_MS);
  }

  _clearSyncTimer() {
    if (this._syncTimer) {
      window.clearTimeout(this._syncTimer);
      this._syncTimer = null;
    }
  }

  _clearMountRetries() {
    if (this._mountRetryTimer) {
      window.clearTimeout(this._mountRetryTimer);
      this._mountRetryTimer = null;
    }
    this._mountRetryCount = 0;
  }

  _isSummaryReady() {
    if (!getRechargeWidgetConfig()?.formattedPrices) return false;
    if (!this._getVariantId()) return false;
    return Boolean(this._readSummaryFromRechargeConfig());
  }

  _scheduleMountRetries() {
    this._clearMountRetries();
    this._runMountRetry();
  }

  _runMountRetry() {
    this._evaluateOptionCount();
    this._syncSummary();

    if (this._isSummaryReady() || this._mountRetryCount >= MAX_MOUNT_RETRIES) {
      return;
    }

    this._mountRetryCount += 1;
    this._mountRetryTimer = window.setTimeout(() => {
      this._mountRetryTimer = null;
      this._runMountRetry();
    }, MOUNT_RETRY_MS);
  }

  _detachRechargeListeners() {
    this._rechargeListeners.forEach(({ target, type, handler, options }) => {
      target.removeEventListener(type, handler, options);
    });
    this._rechargeListeners = [];
  }

  _countPurchaseOptions() {
    const widgetConfig = getRechargeWidgetConfig();
    const variantId = this._getVariantId();
    const variantData = this._getVariantData(widgetConfig?.formattedPrices, variantId);

    if (variantData) {
      let count = variantData.sellingPlans
        ? Object.keys(variantData.sellingPlans).length
        : 0;

      if (
        variantData.oneTimePrice ||
        variantData.onetimePrice ||
        variantData.one_time_price ||
        (!variantData.sellingPlans && variantData.price)
      ) {
        count += 1;
      }

      if (count > 0) return count;
    }

    const themeHost = this._panel?.querySelector(".wt-subscription-widget");
    if (themeHost) {
      return themeHost.querySelectorAll(".wt-subscription-widget__plans").length;
    }

    return 0;
  }

  _evaluateOptionCount() {
    if (!this._panel || !this._isMobile()) {
      if (!this.hidden) this.hidden = true;
      this.classList.remove(VISIBLE_CLASS);
      this._syncBodyStickyState(true);
      return;
    }

    const optionCount = this._countPurchaseOptions();
    const sectionRoot = this.closest("[data-pdp-subscription-cta]");

    if (optionCount === 1) {
      if (!this.hidden) this.hidden = true;
      this.classList.remove(VISIBLE_CLASS, ACTIVE_CLASS);
      if (!this._panel.classList.contains(SINGLE_OPTION_CLASS)) {
        this._panel.classList.add(SINGLE_OPTION_CLASS);
      }
      this._panel.classList.remove(OPEN_CLASS);
      this._open = false;
      sectionRoot?.classList.add("subscription-inline-active");
      this._syncBodyStickyState();
      return;
    }

    if (this._panel.classList.contains(SINGLE_OPTION_CLASS)) {
      this._panel.classList.remove(SINGLE_OPTION_CLASS);
    }
    sectionRoot?.classList.remove("subscription-inline-active");

    if (this.hidden) this.hidden = false;
    this.removeAttribute("hidden");
    this.classList.add(VISIBLE_CLASS);
    this._syncBodyStickyState();
  }

  _syncSummary() {
    if (!this._isMobile()) return;

    const summary = this._readSelectedSummary();
    if (!summary) return;

    const key = `${summary.label || ""}|${summary.price || ""}|${summary.badge || ""}`;
    if (key === this._lastSummaryKey) return;
    this._lastSummaryKey = key;

    if (summary.label && this._planEl && this._planEl.textContent !== summary.label) {
      this._planEl.textContent = summary.label;
    }
    if (summary.price && this._priceEl && this._priceEl.textContent !== summary.price) {
      this._priceEl.textContent = summary.price;
    }

    if (this._badgeEl) {
      if (summary.badge) {
        if (this._badgeEl.textContent !== summary.badge) {
          this._badgeEl.textContent = summary.badge;
        }
        this._badgeEl.hidden = false;
      } else if (!this._badgeEl.hidden || this._badgeEl.textContent !== "") {
        this._badgeEl.textContent = "";
        this._badgeEl.hidden = true;
      }
    }

    this._updateLiveRegion(summary);
  }

  _updateLiveRegion(summary) {
    if (!this._liveEl) return;

    const parts = [summary.label, summary.price, summary.badge].filter(Boolean);
    const announcement = parts.join(", ");
    if (this._liveEl.textContent !== announcement) {
      this._liveEl.textContent = announcement;
    }
  }

  _readSelectedSummary() {
    if (!this._isMobile()) return null;
    return this._readSummaryFromRechargeConfig();
  }

  _getVariantData(formattedPrices, variantId) {
    if (!formattedPrices || !variantId) return null;
    return formattedPrices[variantId] || formattedPrices[String(variantId)] || null;
  }

  _getPlanData(sellingPlans, sellingPlanId) {
    if (!sellingPlans || !sellingPlanId) return null;
    return sellingPlans[sellingPlanId] || sellingPlans[String(sellingPlanId)] || null;
  }

  _readSummaryFromRechargeConfig() {
    const widgetConfig = getRechargeWidgetConfig();
    if (!widgetConfig) return null;

    const formattedPrices = widgetConfig.formattedPrices || {};
    const shopifyPlans = widgetConfig.shopifyPlans || [];
    const variantId = this._getVariantId();

    if (!variantId) return null;

    const variantData = this._getVariantData(formattedPrices, variantId);
    if (!variantData) return null;

    let sellingPlanId = this._getSellingPlanId();

    if (!sellingPlanId) {
      const oneTimePrice =
        variantData.oneTimePrice ||
        variantData.onetimePrice ||
        variantData.one_time_price ||
        (variantData.sellingPlans ? null : variantData.price);

      if (oneTimePrice) {
        return {
          label: this._cleanPlanLabel(
            variantData.oneTimeLabel || variantData.one_time_label || "One-time purchase",
          ),
          price: this._formatConfigPrice(oneTimePrice),
          badge: "",
        };
      }

      const planKeys = variantData.sellingPlans
        ? Object.keys(variantData.sellingPlans)
        : [];
      if (planKeys.length) sellingPlanId = planKeys[0];
    }

    if (!sellingPlanId || !variantData.sellingPlans) return null;

    const planData = this._getPlanData(variantData.sellingPlans, sellingPlanId);
    if (!planData) return null;

    const shopifyPlan = shopifyPlans.find(
      (plan) => String(plan.id) === String(sellingPlanId),
    );

    const planName =
      shopifyPlan?.name || shopifyPlan?.title || planData.name || planData.title || "";

    const price =
      planData.price || planData.formattedPrice || planData.discountedPrice || "";

    let badge = "";
    const discountValue =
      planData.discountValue ?? planData.discountPercentage ?? planData.discount_percent;

    if (discountValue != null && discountValue !== "") {
      badge = this._normalizeBadge(`Save ${discountValue}%`);
    }

    return {
      label: this._cleanPlanLabel(planName),
      price: this._formatConfigPrice(price),
      badge,
    };
  }

  _formatConfigPrice(price) {
    if (price == null || price === "") return "";
    if (typeof price === "string") return price.replace(/\s+/g, "");
    return String(price);
  }

  _cleanPlanLabel(text) {
    if (!text) return "";
    return String(text)
      .replace(/save\s*\d+\s*%/gi, "")
      .replace(/recommended/gi, "")
      .replace(/(\$|£|€)\s?[\d,.]+/g, "")
      .replace(/\s+/g, " ")
      .trim()
      .toUpperCase();
  }

  _normalizeBadge(text) {
    if (!text) return "";
    const match = String(text).match(/save\s*\d+\s*%/i);
    if (!match) return String(text).trim();
    return match[0].replace(/\s+/g, " ").replace(/save/i, "Save");
  }

  _handleToggleClick(event) {
    event.preventDefault();
    if (!this._isMobile() || !this._panel) return;

    this._syncSummary();

    if (this._panel.isSheetOpen?.()) {
      this._closeSheet(true);
    } else {
      this._openSheet();
    }
  }

  _openSheet() {
    if (!this._panel || !this._isMobile()) return;

    this._syncSummary();
    this._open = true;
    this._panel.open?.();
    this._syncBodyStickyState();
  }

  _closeSheet(returnFocus) {
    if (!this._panel) return;

    this._open = false;
    this._panel.close?.(returnFocus);
    this._syncBodyStickyState();
    this._scheduleUpdate();
  }
}

if (!customElements.get("subscription-pdp-panel")) {
  customElements.define("subscription-pdp-panel", SubscriptionPdpPanel);
}

if (!customElements.get("sticky-subscription-cta")) {
  customElements.define("sticky-subscription-cta", StickySubscriptionCta);
}
